import { Connection, Proxy } from 'signalr-no-jquery';
import { SendMessageCommandParameters } from '../types';

class SignalRService {

    private connection: any;
    private hubProxy: any;
    private setOnConnected: any;
    private roomId: string = '';
    private lifecycleWired: boolean = false;
    private startPromise: Promise<void> | null = null;
    private joinedRoomId: string = '';
    private updateChannels = new Set<string>();
    private reconnectTimer: number | null = null;
    private shouldReconnect: boolean = true;
    private readonly reconnectDelayMs = 5000;

    constructor(signalRConnection: Connection, hubProxy: Proxy, setOnConnected?: React.Dispatch<React.SetStateAction<boolean>>) {
        this.connection = signalRConnection
        this.hubProxy = hubProxy
        this.setOnConnected = setOnConnected
    }

    startConnection = async (roomId?: string) => {
        this.shouldReconnect = true;
        this.clearReconnectTimer();

        if (roomId) {
            this.roomId = roomId;
        }
        this.wireLifecycleEvents();

        try {
            await this.ensureStarted();
            if (roomId) {
                await this.joinChatRoom(roomId);
            } else {
                this.setOnConnected?.(true);
            }
        } catch (err) {
            console.error('Error starting SignalR connection:', err);
            this.setOnConnected?.(false);
            this.scheduleReconnect();
        }
    };

    // Wire connection lifecycle events so `connected` state reflects
    // disconnect / reconnect in addition to the initial connect.
    private wireLifecycleEvents = () => {
        if (this.lifecycleWired) return;
        this.lifecycleWired = true;

        if (typeof this.connection.disconnected === 'function') {
            this.connection.disconnected(() => {
                console.log('SignalR disconnected');
                this.startPromise = null;
                this.joinedRoomId = '';
                this.setOnConnected?.(false);
                this.scheduleReconnect();
            });
        }

        if (typeof this.connection.reconnecting === 'function') {
            this.connection.reconnecting(() => {
                console.log('SignalR reconnecting');
                this.setOnConnected?.(false);
            });
        }

        if (typeof this.connection.reconnected === 'function') {
            this.connection.reconnected(async () => {
                console.log('SignalR reconnected, re-joining subscriptions');
                // Re-join the room so server-side group membership is restored,
                // then mark connected so the pending queue flushes.
                this.joinedRoomId = '';
                try {
                    await this.rejoinSubscriptions();
                } catch (error) {
                    console.log("Error while re-joining after reconnect", error);
                }
            });
        }
    };

    private ensureStarted = async () => {
        this.shouldReconnect = true;

        if (!this.startPromise) {
            this.startPromise = new Promise<void>((resolve, reject) => {
                this.connection.start()
                    .done(() => {
                        resolve();
                    })
                    .fail((error: any) => {
                        this.startPromise = null;
                        reject(error);
                    });
            });
        }

        return this.startPromise;
    };

    private clearReconnectTimer = () => {
        if (this.reconnectTimer === null) {
            return;
        }

        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
    };

    private scheduleReconnect = () => {
        if (!this.shouldReconnect || this.reconnectTimer !== null) {
            return;
        }

        this.reconnectTimer = window.setTimeout(async () => {
            this.reconnectTimer = null;

            if (!this.shouldReconnect) {
                return;
            }

            console.log('SignalR attempting reconnect');
            try {
                this.joinedRoomId = '';
                await this.rejoinSubscriptions();
                console.log('SignalR reconnect successful');
            } catch (error) {
                this.startPromise = null;
                this.setOnConnected?.(false);
                console.log('SignalR reconnect failed', error);
                this.scheduleReconnect();
            }
        }, this.reconnectDelayMs);
    };

    joinChatRoom = async (roomId: string) => {
        if (!roomId) {
            return;
        }

        if (this.joinedRoomId === roomId) {
            this.setOnConnected?.(true);
            return;
        }

        try {
            await this.hubProxy.invoke('joinChatRoom', roomId);
            this.joinedRoomId = roomId;
            this.setOnConnected?.(true);
            console.log("Joined successfully.");
        } catch (error) {
            this.setOnConnected?.(false);
            this.joinedRoomId = '';
            console.log("Error while joining", error);
            throw error;
        }
    };

    joinUpdateChannel = async (channelName: string) => {
        if (!channelName) {
            return;
        }

        if (this.updateChannels.has(channelName)) {
            return;
        }

        try {
            await this.ensureStarted();
            await this.hubProxy.invoke('joinUpdateChannel', channelName);
            this.updateChannels.add(channelName);
            this.setOnConnected?.(true);
            console.log("Joined update channel successfully.", channelName);
        } catch (error) {
            console.log("Error while joining update channel", channelName, error);
            throw error;
        }
    };

    leaveUpdateChannel = async (channelName: string) => {
        if (!channelName) {
            return;
        }

        this.updateChannels.delete(channelName);

        try {
            await this.ensureStarted();
            await this.hubProxy.invoke('leaveUpdateChannel', channelName);
            console.log("Left update channel successfully.", channelName);
        } catch (error) {
            console.log("Error while leaving update channel", channelName, error);
        }
    };

    on = (eventName: string, handler: (...args: any[]) => void) => {
        this.hubProxy.on(eventName, handler);
    };

    off = (eventName: string, handler: (...args: any[]) => void) => {
        if (typeof this.hubProxy.off === "function") {
            this.hubProxy.off(eventName, handler);
        }
    };

    stopConnection = () => {
        this.shouldReconnect = false;
        this.clearReconnectTimer();
        this.startPromise = null;
        this.joinedRoomId = '';
        this.updateChannels.clear();
        this.roomId = '';
        this.setOnConnected?.(false);

        if (typeof this.connection.stop === "function") {
            this.connection.stop();
        }
    };

    private rejoinSubscriptions = async () => {
        await this.ensureStarted();

        for (const channel of Array.from(this.updateChannels)) {
            await this.hubProxy.invoke('joinUpdateChannel', channel);
        }

        if (this.roomId) {
            await this.joinChatRoom(this.roomId);
            return;
        }

        this.setOnConnected?.(true);
    };

    sendMessage = async (message: SendMessageCommandParameters) => {
        try {
            await this.ensureStarted();

            if (message.ChatRoomId) {
                await this.joinChatRoom(message.ChatRoomId);
            }

            await this.hubProxy.invoke('Send', message);
        } catch (error) {
            console.log("Error while sending the message", error);
            throw error;
        }
    }
};

export default SignalRService;
